import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reportsApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'export' | 'schedule';

/** FR-REP-05 + FR-REP-06 — Exports and scheduled reports */
export function ExportsSchedulesPage() {
  const [tab, setTab] = useState<Tab>('export');

  return (
    <div className="p-6">
      <PageHeader
        title="Exports & schedules"
        subtitle="FR-REP-05/06 — Download reports as CSV/Excel/PDF and schedule recurring emails"
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200">
        {([['export', 'Ad-hoc export'], ['schedule', 'Scheduled reports']] as [Tab, string][]).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === k ? 'border-sage-500 text-sage-600 font-semibold'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>{label}</button>
        ))}
      </div>

      {tab === 'export' && <ExportTab />}
      {tab === 'schedule' && <ScheduleTab />}
    </div>
  );
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

function ExportTab() {
  const typesQ = useQuery({ queryKey: ['export-types'], queryFn: () => reportsApi.listExportTypes() });
  const [reportType, setReportType] = useState('sales.daily');
  const def = defaultRange();
  const [startDate, setStartDate] = useState(def.start);
  const [endDate, setEndDate] = useState(def.end);
  const [asOfDate, setAsOfDate] = useState(def.end);

  const needsRange = ['sales.', 'finance.pnl', 'finance.cash-flow', 'inventory.abc', 'hr.attendance'].some((p) => reportType.startsWith(p));
  const needsAsOf = ['finance.ap-aging', 'finance.ar-aging'].includes(reportType);

  const buildParams = () => {
    if (needsRange) return { startDate, endDate };
    if (needsAsOf) return { asOfDate };
    return {};
  };

  const downloadMut = useMutation({
    mutationFn: (format: 'csv'|'xlsx'|'pdf') => reportsApi.downloadExport(format, reportType, buildParams()),
    onSuccess: () => toast.success('Download started'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>

      <div className="card p-4 space-y-3">
        <div>
          <label className="label">Report type</label>
          {typesQ.isLoading ? <LoadingState /> : (
            <select className="input" value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {typesQ.data?.map((t: any) => (
                <option key={t.type} value={t.type}>{t.title} ({t.type})</option>
              ))}
            </select>
          )}
        </div>

        {needsRange && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Start date</label>
              <input type="date" className="input" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="label">End date</label>
              <input type="date" className="input" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        )}
        {needsAsOf && (
          <div>
            <label className="label">As of date</label>
            <input type="date" className="input" value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)} />
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-slate-200">
          <button className="btn-primary" disabled={downloadMut.isPending}
            onClick={() => downloadMut.mutate('csv')}>Download CSV</button>
          <button className="btn-primary" disabled={downloadMut.isPending}
            onClick={() => downloadMut.mutate('xlsx')}>Download Excel</button>
          <button className="btn-primary" disabled={downloadMut.isPending}
            onClick={() => downloadMut.mutate('pdf')}>Download PDF</button>
        </div>
      </div>
    </div>
  );
}

function ScheduleTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deletingSchedule, setDeletingSchedule] = useState<any | null>(null);

  const q = useQuery({ queryKey: ['schedules'], queryFn: () => reportsApi.listSchedules() });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: any) => reportsApi.updateSchedule(id, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => reportsApi.deleteSchedule(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['schedules'] });
      setDeletingSchedule(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>

      <div className="mb-3 flex justify-end">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New schedule</button>
      </div>

      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-x-auto">
          <table className="table">
            <thead><tr><th>Name</th><th>Report</th><th>Format</th><th>Frequency</th>
              <th>Recipients</th><th>Next run</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={8}><EmptyState message="No scheduled reports." /></td></tr>
              ) : q.data.map((s: any) => (
                <tr key={s.id}>
                  <td className="text-xs">{s.name}</td>
                  <td className="font-mono text-2xs">{s.report_type}</td>
                  <td><span className="badge bg-canvas-subtle text-2xs">{s.export_format}</span></td>
                  <td className="text-xs">
                    {s.frequency}
                    {s.frequency === 'weekly' && ` (day ${s.day_of_week})`}
                    {s.frequency === 'monthly' && ` (day ${s.day_of_month})`}
                    <div className="text-2xs text-slate-500">{s.time_of_day}</div>
                  </td>
                  <td className="text-2xs">
                    {Array.isArray(s.recipient_emails) ? s.recipient_emails.slice(0, 2).join(', ') + (s.recipient_emails.length > 2 ? `+${s.recipient_emails.length - 2}` : '') : '—'}
                  </td>
                  <td className="text-2xs text-slate-500">
                    {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : '—'}
                  </td>
                  <td>
                    <input type="checkbox" checked={s.is_active}
                      onChange={(e) => toggleMut.mutate({ id: s.id, isActive: e.target.checked })} />
                  </td>
                  <td>
                    <button
                      className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                      onClick={() => setDeletingSchedule(s)}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateScheduleModal
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['schedules'] }); setShowCreate(false); }} />}

      {deletingSchedule && (
        <Modal
          onClose={() => setDeletingSchedule(null)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Reports · Delete schedule</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Delete schedule?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              Delete <span className="font-semibold text-ink-900">{deletingSchedule.name}</span>?
              This schedule will stop running immediately.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setDeletingSchedule(null)}>Cancel</button>
            <button
              className="btn-primary !bg-danger hover:!bg-red-700"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate(deletingSchedule.id)}
            >
              {deleteMut.isPending ? 'Deleting…' : 'Delete schedule'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CreateScheduleModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const typesQ = useQuery({ queryKey: ['export-types'], queryFn: () => reportsApi.listExportTypes() });
  const [name, setName] = useState('');
  const [reportType, setReportType] = useState('sales.daily');
  const [exportFormat, setExportFormat] = useState<'csv'|'xlsx'|'pdf'>('pdf');
  const [recipientEmails, setRecipientEmails] = useState('');
  const [frequency, setFrequency] = useState<'daily'|'weekly'|'monthly'>('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(1);  // Monday
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState('08:00');

  const mut = useMutation({
    mutationFn: () => {
      const emails = recipientEmails.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      return reportsApi.createSchedule({
        name, reportType, exportFormat,
        recipientEmails: emails,
        frequency,
        dayOfWeek: frequency === 'weekly' ? dayOfWeek : undefined,
        dayOfMonth: frequency === 'monthly' ? dayOfMonth : undefined,
        timeOfDay,
      });
    },
    onSuccess: (d: any) => {
      toast.success(`Created — next run ${new Date(d.nextRunAt).toLocaleString()}`, { duration: 6000 });
      onCreated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Reports · Schedule</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Schedule a report</h2>
      </div>
      <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1 min-h-0">
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Weekly sales summary" />
        </div>
        <div>
          <label className="label">Report type</label>
          <select className="input" value={reportType} onChange={(e) => setReportType(e.target.value)}>
            {typesQ.data?.map((t: any) => (
              <option key={t.type} value={t.type}>{t.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Export format</label>
          <select className="input" value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as any)}>
            <option value="csv">CSV</option>
            <option value="xlsx">Excel</option>
            <option value="pdf">PDF</option>
          </select>
        </div>
        <div>
          <label className="label">Recipient emails <span className="font-normal text-ink-400 normal-case tracking-normal">(comma or space separated)</span></label>
          <textarea className="input text-xs" rows={2} value={recipientEmails}
            onChange={(e) => setRecipientEmails(e.target.value)}
            placeholder="admin@example.com, cfo@example.com" />
        </div>
        <div>
          <label className="label">Frequency</label>
          <select className="input" value={frequency}
            onChange={(e) => setFrequency(e.target.value as any)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {frequency === 'weekly' && (
          <div>
            <label className="label">Day of week</label>
            <select className="input" value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value))}>
              {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i) => (
                <option key={i} value={i}>{d}</option>
              ))}
            </select>
          </div>
        )}
        {frequency === 'monthly' && (
          <div>
            <label className="label">Day of month (1–31)</label>
            <input type="number" min={1} max={31} className="input tabular-nums" value={dayOfMonth}
              onChange={(e) => setDayOfMonth(parseInt(e.target.value) || 1)} />
          </div>
        )}
        <div>
          <label className="label">Time of day</label>
          <input type="time" className="input" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} />
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || !recipientEmails.trim() || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create schedule'}
        </button>
      </div>
    </Modal>
  );
}
